import type { FiscalStatus, VerificationStatus } from '../../types'
import { StatusPill } from '../../ui/ui'
import { enumLabel, translate, useT } from '../../i18n'
import type { TFunction } from '../../i18n'

/** Secteurs proposés à la création d'une requête. */
export const QUERY_CATEGORIES = [
  'COMMERCE', 'FINANCE', 'ASSURANCE', 'IMMOBILIER', 'RESTAURATION', 'EDUCATION',
  'SANTE', 'TRANSPORT', 'TELECOM', 'TECH', 'MODE', 'BEAUTE', 'BTP', 'JEUX',
  'LIVRAISON', 'SERVICES',
]

/** Secteurs à surveiller en priorité : activité réglementée. */
export const SENSITIVE_CATEGORIES = ['JEUX']

/** Canaux où un annonceur peut être vu, dans l'ordre d'affichage des filtres. */
export const PLATFORMS = ['GOOGLE', 'META', 'DISPLAY_NETWORK']

export const FISCAL_STATUSES: FiscalStatus[] = ['UNKNOWN', 'REGISTERED', 'NOT_REGISTERED', 'UNDER_REVIEW']

export const VERIFICATION_STATUSES: VerificationStatus[] = [
  'NEW', 'TO_REVIEW', 'IN_REVIEW', 'CONFIRMED', 'FALSE_POSITIVE', 'ESCALATED', 'ARCHIVED',
]

/** Catégories de sites cibles pour le relevé des bannières. */
export const TARGET_CATEGORIES = ['NEWS', 'FORUM', 'ECOMMERCE', 'CLASSIFIEDS', 'BLOG', 'PORTAL', 'OTHER']

/** Transitions autorisées, identiques à celles du serveur. */
export const VERIF_TRANSITIONS: Record<VerificationStatus, VerificationStatus[]> = {
  NEW: ['TO_REVIEW'],
  TO_REVIEW: ['IN_REVIEW', 'FALSE_POSITIVE'],
  IN_REVIEW: ['CONFIRMED', 'FALSE_POSITIVE', 'ESCALATED'],
  CONFIRMED: ['ARCHIVED', 'ESCALATED'],
  FALSE_POSITIVE: ['ARCHIVED', 'TO_REVIEW'],
  ESCALATED: ['CONFIRMED', 'ARCHIVED'],
  ARCHIVED: ['TO_REVIEW'],
}

/**
 * Libellés des valeurs du module, traduits à l'affichage.
 *
 * Un secteur ou un canal que le serveur ajoute avant l'interface s'affiche avec
 * son code plutôt que de laisser un trou.
 */
export const categoryLabel = (t: TFunction, key: string | null | undefined) =>
  enumLabel(t, 'ads.cat', key, key ?? '—')
export const platformLabel = (t: TFunction, key: string | null | undefined) =>
  enumLabel(t, 'ads.platform', key, key ?? '—')
export const detectionTypeLabel = (t: TFunction, key: string | null | undefined) =>
  enumLabel(t, 'ads.type', key, key ?? '—')
export const fiscalLabel = (t: TFunction, key: string | null | undefined) =>
  enumLabel(t, 'ads.fiscal', key ?? 'UNKNOWN', key ?? '—')
export const verifLabel = (t: TFunction, key: string | null | undefined) =>
  enumLabel(t, 'ads.verif', key ?? 'NEW', key ?? '—')
export const targetCategoryLabel = (t: TFunction, key: string | null | undefined) =>
  enumLabel(t, 'ads.target', key, key ?? '—')

/** Nombre de pages de résultats Google lues par requête et par pays. */
export const GOOGLE_PAGES_PER_QUERY = 3

export function VerificationPill({ status }: { status: VerificationStatus | null | undefined }) {
  const t = useT()
  const s = status ?? 'NEW'
  const tone = s === 'CONFIRMED' ? 'crit' : s === 'TO_REVIEW' || s === 'ESCALATED' ? 'warn' : s === 'FALSE_POSITIVE' || s === 'ARCHIVED' ? 'ok' : 'info'
  return <StatusPill tone={tone}>{verifLabel(t, s)}</StatusPill>
}

export function FiscalPill({ status }: { status: FiscalStatus | null | undefined }) {
  const t = useT()
  const s = status ?? 'UNKNOWN'
  const tone = s === 'NOT_REGISTERED' ? 'crit' : s === 'REGISTERED' ? 'ok' : s === 'UNDER_REVIEW' ? 'warn' : 'info'
  return <StatusPill tone={tone}>{fiscalLabel(t, s)}</StatusPill>
}

export function hostname(url: string | null | undefined): string {
  if (!url) return ''
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

/**
 * Durée lisible. `translate` et non `useT` : la fonction est appelée dans des
 * cellules de tableau sans contexte à portée, et l'écran est remonté quand la
 * langue change.
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return translate('ads.durationMs', { n: ms })
  const s = Math.round(ms / 1000)
  if (s < 60) return translate('ads.durationS', { n: s })
  return translate('ads.durationMin', { m: Math.floor(s / 60), s: String(s % 60).padStart(2, '0') })
}

export function screenshotUrl(path: string): string {
  const base = import.meta.env.VITE_API_URL ?? '/api'
  return `${base}/ad-detection/screenshots/${path}`
}
